import { useMemo, type MouseEvent } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { formatDistanceToNowStrict } from 'date-fns';
import { fr as frLocale } from 'date-fns/locale';
import { useAuth } from '../../../shared/hooks/useAuth';
import { useApiError } from '../../../shared/errors/useApiError';
import { useNotification } from '../../../shared/notifications/NotificationProvider';
import type { Post } from '../../../shared/services/opportunities.service';
import { avatarColor, initials } from '../postVisuals';
import { stripMarkdown } from '../stripMarkdown';
import { useOpportunityMutations } from '../useOpportunities';
import { useShareOpportunity } from '../usePostShare';

type Props = {
  post: Post;
};

/**
 * Feed row — Threads / LinkedIn-style card. The whole card is a link to the
 * detail page; engagement buttons are display-only in phase 1 and will be
 * wired up in phase 3.
 */
export function PostCard({ post }: Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const { toggleLike, toggleSave } = useOpportunityMutations();
  const share = useShareOpportunity();
  const dateLocale = i18n.language?.startsWith('fr') ? frLocale : undefined;
  const timeAgo = formatDistanceToNowStrict(new Date(post.createdAt), {
    locale: dateLocale,
    addSuffix: true,
  });
  // The card preview is plain text — markdown decorations would be noisy and
  // line-clamp doesn't know where a `**bold**` ends. We render the formatted
  // body only on the detail page through {@code PostBody}.
  const preview = useMemo(() => stripMarkdown(post.body), [post.body]);

  // Engagement buttons live inside an <a> (the card-wide link). Every click
  // handler swallows propagation + default so we don't navigate to the
  // detail page when the user just wanted to like / save / share.
  const swallow = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const bounceToLogin = () => {
    notify.info(t('opportunities.actions.loginToPost'));
    navigate(
      '/login?next=' + encodeURIComponent(`/opportunities/${post.id}`),
    );
  };
  const handleLike = (e: MouseEvent) => {
    swallow(e);
    if (!isAuthenticated) return bounceToLogin();
    toggleLike.mutate(post.id, {
      onError: (err) => notify.error(translate(err)),
    });
  };
  const handleSave = (e: MouseEvent) => {
    swallow(e);
    if (!isAuthenticated) return bounceToLogin();
    toggleSave.mutate(post.id, {
      onError: (err) => notify.error(translate(err)),
    });
  };
  const handleShare = (e: MouseEvent) => {
    swallow(e);
    void share({ postId: post.id, title: post.title, bodyPreview: preview });
  };

  return (
    <Box
      component={RouterLink}
      to={`/opportunities/${post.id}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        // `minWidth: 0` is required so a grid item with `minmax(0, 1fr)`
        // can actually shrink to its column width — without it, a long
        // unbreakable string inside the card would push the card wider
        // than its grid column and break the uniform 3-column layout.
        minWidth: 0,
        // Belt-and-suspenders: even if text wrap fails, anything past the
        // card's borders is clipped rather than leaking into a neighbour.
        overflow: 'hidden',
        textDecoration: 'none',
        color: 'inherit',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        p: { xs: 2, sm: 2.5 },
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        },
      }}
    >
      {/* Author row */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
        <Avatar
          src={post.author.avatarUrl ?? undefined}
          sx={{
            width: 40,
            height: 40,
            bgcolor: avatarColor(post.author.userId),
            color: 'common.white',
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {initials(post.author.name)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.2 }}>
            {post.author.name}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.25 }}
          >
            {timeAgo}
          </Typography>
        </Box>
        <Chip
          label={t(`opportunities.category.${post.category}`)}
          size="small"
          sx={{
            flex: '0 0 auto',
            fontWeight: 600,
            fontSize: 12,
            height: 24,
          }}
        />
      </Stack>

      {/* Title — always reserves the vertical space of two lines (minHeight)
          even when the post title fits on one. That, combined with the body
          preview's own fixed-line minHeight below, is what keeps every card
          in the feed exactly the same height: title slot + body slot +
          engagement strip all have deterministic sizes. */}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: { xs: 16, sm: 17 },
          lineHeight: 1.3,
          mb: 0.75,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          // Break overly-long words / URLs onto a new line so they can't
          // widen the column past its 1fr share.
          overflowWrap: 'anywhere',
          // 2 lines × 1.3 line-height = 2.6em — reserved whether or not the
          // title actually fills two lines.
          minHeight: '2.6em',
        }}
      >
        {post.title}
      </Typography>
      {/* Body preview — plain-text strip of the markdown source, fixed at
          exactly three lines tall (both clamp + minHeight) so a card with
          a short body sits flush against the same baseline as one whose
          body overflows. The full markdown rendering happens on the detail
          page. */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          mb: 1.75,
          // No pre-wrap: cards present a flat preview. Newlines from the
          // source would otherwise eat into the 3-line budget and make the
          // card visually empty even when the post has real content.
          whiteSpace: 'normal',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          // Same wrap-anywhere rule as the title: a 50-char "aaaaaaa…" run
          // must wrap rather than push the column out.
          overflowWrap: 'anywhere',
          // 3 lines × 1.43 (MUI body2 default line-height) ≈ 4.3em — held
          // even when the body is one short sentence.
          minHeight: '4.3em',
        }}
      >
        {preview}
      </Typography>

      {/* Cover images live on the detail page only — feed cards are
          deliberately uniform (no image) so layout is predictable and the
          row reads as a clean grid. */}

      {/* Engagement actions — clickable like / save / share, plus a
          comment counter that's display-only until phase 4 ships the
          comments thread. All buttons swallow click + default so they
          don't bubble up to the card's RouterLink wrapper. */}
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ color: 'text.secondary', fontSize: 13, mt: 'auto' }}
      >
        <Stack direction="row" alignItems="center" spacing={0.25}>
          <IconButton
            size="small"
            onClick={handleLike}
            disabled={toggleLike.isPending}
            aria-label={post.likedByMe
              ? t('opportunities.actions.unlike')
              : t('opportunities.actions.like')}
            aria-pressed={post.likedByMe}
            sx={{ color: post.likedByMe ? 'error.main' : 'inherit' }}
          >
            {post.likedByMe
              ? <FavoriteRoundedIcon sx={{ fontSize: 18 }} />
              : <FavoriteBorderRoundedIcon sx={{ fontSize: 18 }} />}
          </IconButton>
          <span>{post.likeCount}</span>
        </Stack>
        {/* Comment counter — not clickable until phase 4. Tapping it would
            navigate to the detail page anyway via the parent link. */}
        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ pl: 1 }}>
          <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 18 }} />
          <span>{post.commentCount}</span>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ pl: 0.5 }}>
          <IconButton
            size="small"
            onClick={handleSave}
            disabled={toggleSave.isPending}
            aria-label={post.savedByMe
              ? t('opportunities.actions.unsave')
              : t('opportunities.actions.save')}
            aria-pressed={post.savedByMe}
            sx={{ color: post.savedByMe ? 'primary.main' : 'inherit' }}
          >
            {post.savedByMe
              ? <BookmarkRoundedIcon sx={{ fontSize: 18 }} />
              : <BookmarkBorderRoundedIcon sx={{ fontSize: 18 }} />}
          </IconButton>
          <span>{post.saveCount}</span>
        </Stack>
        <Box sx={{ ml: 'auto' }}>
          <IconButton
            size="small"
            onClick={handleShare}
            aria-label={t('opportunities.actions.share')}
          >
            <ShareOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Stack>
    </Box>
  );
}
